import { createConnector } from "wagmi";
import { injected } from "wagmi/connectors";

import { injectedBrave } from "@/lib/wallet/brave-evm";
import {
  chosenOnly,
  keepPermissions,
  seedChosen,
} from "@/lib/wallet/evm-chosen";
import {
  injectedMetaMask,
  injectedProviders,
  isGenuineMetaMask,
} from "@/lib/wallet/evm-gated-entry";
import { browserWalletReachesPhantom } from "@/lib/wallet/phantom-browser-wallet";
import {
  gatesBrowserWallet,
  isGenericEnabled,
  isOffered,
} from "@/lib/wallet/solana/allowlist";
import { QUIRKS } from "@/lib/wallet/solana/quirks";

import type { Wallet, WalletDetailsParams } from "@rainbow-me/rainbowkit";
import type { EIP1193Provider } from "viem";

/**
 * "Browser Wallet" where the generic Solana layer offers a wallet whose
 * extension also serves Ethereum (D6, `optionc-plan.md` §3.1.9).
 *
 * RainbowKit's entry is a *targeted* injected connector with no gate: it is
 * handed the provider in front (`providers[0]`, else `window.ethereum`), so
 * it listens for that wallet's events and `reconnect` adopts it on
 * `eth_accounts` alone. A Solana approval in OKX, Trust, Backpack, Bitget,
 * Exodus or MetaMask can make that true, and our picker has no entry of its
 * own for most of them, so Browser Wallet is their only EVM route and cannot
 * simply be withdrawn. It is gated instead, like the named entries.
 */
export const BROWSER_EVM_CHOSEN = "mudwater.browser-evm.chosen";

/**
 * The provider RainbowKit's Browser Wallet targets. Read once, when the
 * connector is created, as RainbowKit's own entry reads it: a wallet that
 * injects after `createWagmiConfig` is not this connector's target, and gets
 * no `keepPermissions` either (review A, N6).
 */
function front(): unknown {
  if (typeof window === "undefined") return undefined;
  return injectedProviders(Reflect.get(window, "ethereum"))[0];
}

function isEip1193(value: unknown): value is EIP1193Provider {
  try {
    return (
      typeof value === "object" &&
      value !== null &&
      typeof Reflect.get(value, "request") === "function" &&
      typeof Reflect.get(value, "on") === "function" &&
      typeof Reflect.get(value, "removeListener") === "function"
    );
  } catch {
    // Injected provider getters can throw before their native bridge is ready.
    return false;
  }
}

/* The flags of every wallet whose EVM revoke also ends its Solana session,
   from the quirk table. MetaMask's is checked as genuine MetaMask, since many
   wallets set `isMetaMask`. */
const SITE_WIDE_FLAGS = QUIRKS.filter(
  (entry) => entry.evm?.revokeIsSiteWide && entry.id !== "metamask"
).flatMap((entry) => entry.evm?.flags ?? []);

/** Whether wagmi's disconnect would take the provider's Solana session too. */
export function revokesSiteWide(provider: unknown): boolean {
  if (typeof provider !== "object" || provider === null) return false;
  return (
    isGenuineMetaMask(provider) ||
    SITE_WIDE_FLAGS.some((flag) => Reflect.get(provider, flag) === true)
  );
}

/**
 * Browser Wallet on a gated connector: the same target RainbowKit's entry
 * builds, under `chosenOnly`, with `keepPermissions` where the target's
 * revoke is site-wide. The connector id stays `injected`, which wagmi's
 * storage (and the seed) are written under.
 */
export function gatedBrowserWallet(wallet: Wallet): Wallet {
  return {
    ...wallet,
    installed: wallet.installed === true && isEip1193(front()),
    createConnector: (walletDetails: WalletDetailsParams) =>
      createConnector((config) => {
        const target = front();
        const provider = isEip1193(target) ? target : undefined;
        const base = injected({
          target: () => ({
            id: walletDetails.rkDetails.id,
            name: walletDetails.rkDetails.name,
            // Never fall back to the same unusable window.ethereum provider.
            provider: () => provider,
          }),
        })(config);
        const gated = {
          ...chosenOnly(BROWSER_EVM_CHOSEN, base),
          ...walletDetails,
        };
        if (!revokesSiteWide(provider)) return gated;
        return {
          ...gated,
          async getProvider() {
            const found = await base.getProvider.call(this);
            return found ? keepPermissions(found) : found;
          },
        };
      }),
  };
}

/**
 * Whether Browser Wallet would reach the MetaMask that is offered for
 * Solana, which has a gated entry of its own: then it is left out rather
 * than listed twice, as PR 1 does in front of Phantom.
 */
function reachesMetaMask(): boolean {
  if (!isOffered("metamask")) return false;
  const target = front();
  if (target === undefined) return false;
  return target === injectedMetaMask() || isGenuineMetaMask(target);
}

/**
 * The Browser Wallet entries for this build. With the flag unset
 * (production), the entry as main lists it: the user's decision (2026-10-26)
 * keeps every EVM fix of this branch behind the flag. With it set, first:
 * none where it would reach a genuine Phantom that has its own gated entry
 * (PR 1, `browserWalletReachesPhantom`). Then D6: none in front of an offered
 * MetaMask; gated where `gatesBrowserWallet()` holds (off under `phantom`
 * alone, so the Phantom cut-over changes nothing here); otherwise the entry
 * as it is.
 */
export function browserWalletEntries(entry: () => Wallet): (() => Wallet)[] {
  // The named Brave entry is the only route when Brave owns the front slot.
  const brave = injectedBrave();
  if (brave && front() === brave) return [];
  // A flag-unset build (production) lists the entry as main does.
  if (!isGenericEnabled()) return [entry];
  if (browserWalletReachesPhantom()) return [];
  if (!gatesBrowserWallet()) return [entry];
  if (reachesMetaMask()) return [];
  return [() => gatedBrowserWallet(entry())];
}

/**
 * Carries an existing Browser Wallet connection across the gate, once
 * (`seedChosen`). Only where the gate is on.
 */
export function seedBrowserWalletChoice(): void {
  if (!gatesBrowserWallet()) return;
  seedChosen({
    key: BROWSER_EVM_CHOSEN,
    connectorId: "injected",
    seeded: "mudwater.browser-evm.migrated",
  });
}
